/**
 * UseCaseGraph — an interactive knowledge-graph explorer for the Use Cases page.
 *
 * Rather than presenting industries as flat filter tabs, the domains are
 * rendered as a living knowledge graph — the same mental model the platform
 * itself is built on. The centre is the ontology kernel; an inner ring holds
 * the six invariant platform phases (Ontology → Catalog → Pipelines →
 * Retrieval → Agents → Governance); the outer ring holds each industry use
 * case. Selecting a domain lights up the path through the kernel, visually
 * proving the wrapper thesis: every use case runs the *same* six phases — only
 * the domain specifics change.
 *
 * Interaction model:
 *  - Drag any node to reposition it (pointer + touch).
 *  - Hover a node to inspect it; the caption strip narrates the relationship.
 *  - Click a domain to open its full implementation blueprint below.
 *  - The search box is wired into the graph: matching domains stay lit, the
 *    rest dim — the query filters the graph and the blueprint list together.
 *
 * No runtime graph dependency: a compact, dependency-free SVG renderer with a
 * deterministic radial layout, pointer-driven dragging, an ambient float, and
 * animated "flow" edges. Honours prefers-reduced-motion and is responsive via
 * a fluid viewBox.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Hexagon, Network, RotateCcw, Search as SearchIcon, type LucideIcon,
} from "lucide-react";

export interface GraphPhase {
  key: string;
  n: string;
  phase: string;
  surface: string;
  Icon: LucideIcon;
  generic: string;
}

export interface GraphUseCase {
  id: string;
  industry: string;
  title: string;
  persona: string;
  Icon: LucideIcon;
  blueprint: Record<string, string>;
}

interface Props {
  useCases: GraphUseCase[];
  phases: GraphPhase[];
  query: string;
  onQuery: (q: string) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  industryColor: (industry: string) => string;
}

const VIEW_W = 1000;
const VIEW_H = 640;
const CX = 500;
const CY = 320;
const RING_PHASE = 128;
const RING_CASE = 250;
const R_HUB = 38;
const R_PHASE = 23;
const R_CASE = 27;

const HUB_ID = "__hub";

type NodeKind = "hub" | "phase" | "case";
interface GNode {
  id: string;
  kind: NodeKind;
  label: string;
  Icon: LucideIcon;
  color: string;
  /** phase number or undefined */
  n?: string;
  /** domain reference for case nodes */
  caseId?: string;
  /** phase reference for phase nodes */
  phaseKey?: string;
}

type Pos = Record<string, { x: number; y: number }>;

/** Stable hash → seed for the ambient float, so motion is deterministic. */
function seed(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 997;
  return h;
}

export function UseCaseGraph({
  useCases, phases, query, onQuery, selectedId, onSelect, industryColor,
}: Props) {
  const reduced = useMemo(
    () => typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  // ---- Build nodes (hub + phase ring + case ring). --------------------------
  const nodes = useMemo<GNode[]>(() => {
    const out: GNode[] = [{
      id: HUB_ID, kind: "hub", label: "Ontoligent Core", Icon: Hexagon,
      color: "hsl(var(--accent))",
    }];
    phases.forEach((p) => out.push({
      id: `phase:${p.key}`, kind: "phase", label: p.surface, Icon: p.Icon,
      color: "hsl(var(--accent))", n: p.n, phaseKey: p.key,
    }));
    useCases.forEach((u) => out.push({
      id: `case:${u.id}`, kind: "case", label: u.industry, Icon: u.Icon,
      color: industryColor(u.industry), caseId: u.id,
    }));
    return out;
  }, [phases, useCases, industryColor]);

  // ---- Deterministic radial base layout. ------------------------------------
  const base = useMemo<Pos>(() => {
    const p: Pos = { [HUB_ID]: { x: CX, y: CY } };
    phases.forEach((ph, i) => {
      const a = (-90 + (360 / phases.length) * i) * (Math.PI / 180);
      p[`phase:${ph.key}`] = { x: CX + RING_PHASE * Math.cos(a), y: CY + RING_PHASE * Math.sin(a) };
    });
    useCases.forEach((u, i) => {
      const a = (-90 + (360 / useCases.length) * i) * (Math.PI / 180);
      p[`case:${u.id}`] = { x: CX + RING_CASE * Math.cos(a), y: CY + RING_CASE * Math.sin(a) };
    });
    return p;
  }, [phases, useCases]);

  const [pos, setPos] = useState<Pos>(base);
  useEffect(() => { setPos(base); }, [base]);

  // ---- Ambient float (disabled while dragging / reduced motion). ------------
  const [t, setT] = useState(0);
  useEffect(() => {
    if (reduced) return;
    let raf = 0; let last = 0;
    const loop = (ts: number) => {
      if (ts - last > 60) { setT(ts / 1000); last = ts; }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  // ---- Pointer dragging. ----------------------------------------------------
  const svgRef = useRef<SVGSVGElement | null>(null);
  const drag = useRef<{ id: string; sx: number; sy: number; ox: number; oy: number; moved: boolean } | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);

  function clientToSvgScale(): number {
    const r = svgRef.current?.getBoundingClientRect();
    return r && r.width ? VIEW_W / r.width : 1;
  }

  function onPointerDown(e: React.PointerEvent, id: string) {
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    const p = pos[id];
    drag.current = { id, sx: e.clientX, sy: e.clientY, ox: p.x, oy: p.y, moved: false };
    setDragId(id);
  }
  function onPointerMove(e: React.PointerEvent) {
    const d = drag.current;
    if (!d) return;
    const k = clientToSvgScale();
    const dx = (e.clientX - d.sx) * k;
    const dy = (e.clientY - d.sy) * k;
    if (Math.hypot(dx, dy) > 3) d.moved = true;
    setPos((prev) => ({ ...prev, [d.id]: { x: d.ox + dx, y: d.oy + dy } }));
  }
  function onPointerUp() {
    const d = drag.current;
    if (d && !d.moved && d.id.startsWith("case:")) {
      onSelect(d.id.slice(5));
    }
    drag.current = null;
    setDragId(null);
  }

  // ---- Render position = base/drag position + ambient float. ----------------
  function rp(id: string): { x: number; y: number } {
    const p = pos[id] ?? base[id] ?? { x: CX, y: CY };
    if (reduced || dragId === id || id === HUB_ID) return p;
    const s = seed(id);
    return {
      x: p.x + Math.sin(t * 0.6 + s) * 5,
      y: p.y + Math.cos(t * 0.5 + s * 1.3) * 5,
    };
  }

  // ---- Highlight + dim logic. -----------------------------------------------
  const q = query.trim().toLowerCase();
  const matchCount = useCases.filter((u) => isMatch(u, q)).length;

  const activeCaseId =
    (hoverId?.startsWith("case:") ? hoverId.slice(5) : null) ??
    selectedId;
  const hubLit = activeCaseId != null;

  function caseLit(id: string): boolean {
    return activeCaseId === id || selectedId === id;
  }
  function caseDimmed(u: GraphUseCase): boolean {
    return q.length > 0 && !isMatch(u, q);
  }

  // ---- Caption narration. ---------------------------------------------------
  const caption = useMemo(() => {
    const def = "Drag any node to explore the graph. Click a domain to open its implementation blueprint. Type to filter.";
    if (!hoverId) {
      if (selectedId) {
        const u = useCases.find((x) => x.id === selectedId);
        if (u) return `${u.title} — ${u.persona}. Hover a phase node to read this domain's step for it.`;
      }
      return def;
    }
    if (hoverId === HUB_ID) {
      return "Ontology kernel — the versioned schema, governed retrieval and audit plane every domain plugs into.";
    }
    if (hoverId.startsWith("phase:")) {
      const ph = phases.find((p) => `phase:${p.key}` === hoverId);
      if (!ph) return def;
      const u = selectedId ? useCases.find((x) => x.id === selectedId) : null;
      if (u && u.blueprint[ph.key]) {
        return `Phase ${ph.n} · ${ph.phase} — ${u.blueprint[ph.key]}`;
      }
      return `Phase ${ph.n} · ${ph.phase} — ${ph.generic}`;
    }
    if (hoverId.startsWith("case:")) {
      const u = useCases.find((x) => `case:${x.id}` === hoverId);
      if (u) return `${u.title} — ${u.persona}`;
    }
    return def;
  }, [hoverId, selectedId, phases, useCases]);

  // ---- Edges: hub→phase spokes (kernel), hub→case connectors. ---------------
  const phaseIds = phases.map((p) => `phase:${p.key}`);
  const caseIds = useCases.map((u) => `case:${u.id}`);

  return (
    <div className="kg-fx card overflow-hidden">
      {/* Toolbar: integrated search + legend + reset. */}
      <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2">
          <Network size={16} className="text-accent" />
          <div>
            <div className="text-sm font-semibold leading-tight">Domain knowledge graph</div>
            <div className="text-[11px] text-muted">
              {matchCount} of {useCases.length} domains{q ? " match" : " mapped"} · same six phases, every domain
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="relative flex-1 lg:w-72">
            <SearchIcon size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
            <input
              className="input pl-8"
              placeholder="Filter the graph — persona, pain, outcome…"
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              aria-label="Filter the knowledge graph"
            />
          </label>
          <button
            type="button"
            className="btn-ghost shrink-0"
            title="Reset layout"
            aria-label="Reset graph layout"
            onClick={() => setPos(base)}
          >
            <RotateCcw size={15} />
          </button>
        </div>
      </div>

      {/* The graph canvas. */}
      <div className="relative rounded-md border border-border bg-bg/40">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          className="block w-full touch-none select-none"
          style={{ aspectRatio: `${VIEW_W} / ${VIEW_H}`, maxHeight: "70vh" }}
          role="img"
          aria-label="Interactive knowledge graph of industry use cases connected through the six platform phases"
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <defs>
            <radialGradient id="kg-hub-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="hsl(var(--accent) / 0.30)" />
              <stop offset="100%" stopColor="hsl(var(--accent) / 0)" />
            </radialGradient>
          </defs>

          {/* Hub glow */}
          <circle cx={rp(HUB_ID).x} cy={rp(HUB_ID).y} r={120} fill="url(#kg-hub-glow)" />

          {/* Edges: hub → phases (kernel spokes) */}
          {phaseIds.map((pid) => {
            const a = rp(HUB_ID); const b = rp(pid);
            return (
              <line
                key={`e-${pid}`}
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                className={hubLit ? "kg-edge kg-edge--lit kg-flow" : "kg-edge"}
              />
            );
          })}

          {/* Edges: hub → cases (domain connectors) */}
          {caseIds.map((cid) => {
            const a = rp(HUB_ID); const b = rp(cid);
            const id = cid.slice(5);
            const lit = caseLit(id);
            const dim = caseDimmed(useCases.find((u) => u.id === id)!);
            return (
              <line
                key={`e-${cid}`}
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                className={`kg-edge${lit ? " kg-edge--lit kg-flow" : ""}`}
                opacity={dim ? 0.12 : undefined}
              />
            );
          })}

          {/* Nodes */}
          {nodes.map((node) => {
            const p = rp(node.id);
            const r = node.kind === "hub" ? R_HUB : node.kind === "phase" ? R_PHASE : R_CASE;
            const isHover = hoverId === node.id;
            let dim = false;
            let lit = false;
            if (node.kind === "case") {
              const u = useCases.find((x) => x.id === node.caseId)!;
              dim = caseDimmed(u);
              lit = caseLit(node.caseId!);
            } else if (node.kind === "phase") {
              lit = hubLit;
            }
            // When a domain is selected/hovered, desaturate the other domain
            // nodes so the active one clearly stands out.
            const muted = node.kind === "case" && activeCaseId != null && !lit && !isHover;
            const effColor = muted ? "hsl(var(--muted))" : node.color;
            return (
              <g
                key={node.id}
                transform={`translate(${p.x},${p.y})`}
                style={{ cursor: node.kind === "case" ? "grab" : "grab", opacity: dim ? 0.25 : muted ? 0.5 : 1 }}
                onPointerDown={(e) => onPointerDown(e, node.id)}
                onPointerEnter={() => !drag.current && setHoverId(node.id)}
                onPointerLeave={() => !drag.current && setHoverId(null)}
              >
                {(lit || isHover) && (
                  <circle r={r + 6} className="kg-ring" style={{ color: effColor }} />
                )}
                <circle
                  r={r}
                  className="kg-node"
                  style={{
                    color: effColor,
                    filter: lit || isHover ? "drop-shadow(0 0 6px var(--kg-shadow))" : undefined,
                  }}
                  // expose color to drop-shadow via CSS var
                  ref={(el) => { if (el) el.style.setProperty("--kg-shadow", effColor); }}
                />
                {node.kind === "phase" ? (
                  <text className="kg-num" textAnchor="middle" dy="0.35em">{node.n}</text>
                ) : (
                  <g transform="translate(-10,-10)" style={{ color: effColor }}>
                    <node.Icon width={20} height={20} />
                  </g>
                )}
                <text
                  className={`kg-label ${node.kind === "hub" ? "kg-label--hub" : ""}`}
                  textAnchor="middle"
                  y={r + 16}
                >
                  {node.label}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Caption strip — narrates the current relationship. */}
        <div className="border-t border-border/60 px-3 py-2 text-xs leading-relaxed text-muted">
          {caption}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "hsl(var(--accent))" }} />
          Ontology kernel &amp; phases
        </span>
        {useCases.map((u) => (
          <button
            key={u.id}
            type="button"
            onClick={() => onSelect(u.id)}
            onPointerEnter={() => setHoverId(`case:${u.id}`)}
            onPointerLeave={() => setHoverId(null)}
            className="inline-flex items-center gap-1.5 hover:text-text"
          >
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: industryColor(u.industry) }} />
            {u.industry}
          </button>
        ))}
      </div>

      {/* Scoped styles. */}
      <style>{`
        .kg-fx .kg-edge {
          stroke: hsl(var(--border));
          stroke-width: 1.25;
          fill: none;
        }
        .kg-fx .kg-edge--lit {
          stroke: hsl(var(--accent) / 0.8);
          stroke-width: 1.75;
        }
        .kg-fx .kg-flow {
          stroke-dasharray: 4 6;
          animation: kg-dash 1.1s linear infinite;
        }
        .kg-fx .kg-node {
          fill: hsl(var(--panel));
          stroke: currentColor;
          stroke-width: 2;
          transition: filter .2s ease;
        }
        .kg-fx .kg-ring {
          fill: none;
          stroke: currentColor;
          stroke-width: 1.5;
          opacity: 0.35;
        }
        .kg-fx .kg-num {
          fill: hsl(var(--accent));
          font: 700 13px ui-monospace, SFMono-Regular, Menlo, monospace;
        }
        .kg-fx .kg-label {
          fill: hsl(var(--muted));
          font: 600 12px 'IBM Plex Sans', ui-sans-serif, sans-serif;
        }
        .kg-fx .kg-label--hub {
          fill: hsl(var(--text));
          font-weight: 700;
        }
        @keyframes kg-dash {
          to { stroke-dashoffset: -20; }
        }
        @media (prefers-reduced-motion: reduce) {
          .kg-fx .kg-flow { animation: none; }
        }
      `}</style>
    </div>
  );
}

function isMatch(u: GraphUseCase, q: string): boolean {
  if (!q) return true;
  return (`${u.title} ${u.industry} ${u.persona}`).toLowerCase().includes(q);
}
